import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, CalendarCheck, ShieldCheck, Star, MapPin, Wrench, ChevronDown } from "lucide-react";
import { IMAGES, waLink, scrollToId } from "@/lib/site";

const LINES = ["FAST, RELIABLE", "MOBILE AUTO REPAIR", "& DIAGNOSTICS"];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const BADGES = [
  { icon: ShieldCheck, label: "310S Licensed Master Tech" },
  { icon: Star, label: "5.0 Google Rating" },
  { icon: Wrench, label: "On-Site Diagnostics" },
  { icon: MapPin, label: "Scarborough & GTA Mobile" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden noise-overlay">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img
          src={IMAGES.hero}
          alt="Mobile mechanic performing engine diagnostics"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-spotlight" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-mono2 text-xs sm:text-sm tracking-[0.35em] uppercase text-ember mb-6"
          data-testid="hero-eyebrow"
        >
          Scarborough & GTA — We Come To You
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-[13.5vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-stroke-green" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-mist leading-relaxed"
          data-testid="hero-subheadline"
        >
          Licensed <span className="text-white font-semibold">310S master technician</span> bringing
          expert service directly to your driveway. No towing fees. No waiting rooms.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => scrollToId("#booking")}
            className="inline-flex items-center gap-2 bg-ember text-obsidian font-display font-bold uppercase tracking-wider text-lg px-8 py-4 rounded-sm hover:bg-[#9BE873] hover:shadow-[0_0_36px_rgba(132,220,90,0.4)] hover:-translate-y-0.5 transition-all duration-200"
          >
            <CalendarCheck className="w-5 h-5" />
            Book Service Online
          </button>
          <a
            data-testid="hero-whatsapp-btn"
            href={waLink("Hi GoGoAuto! I'd like a quick quote for my vehicle.")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#25D366]/60 text-[#25D366] font-display font-bold uppercase tracking-wider text-lg px-8 py-4 rounded-sm hover:bg-[#25D366] hover:text-obsidian hover:-translate-y-0.5 transition-all duration-200"
          >
            <MessageCircle className="w-5 h-5" />
            Chat on WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          data-testid="hero-trust-badges"
        >
          {BADGES.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 text-sm text-mist">
              <Icon className="w-4 h-4 text-ember" />
              {label}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-mist hover:text-ember transition-colors"
        aria-label="Scroll down"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="w-7 h-7" />
        </motion.div>
      </motion.button>
    </section>
  );
};
